import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContactDto, CustomerDto, QuoteDto, RegionDto, SiteDto, UserDto } from '../../../dtos';
import { countries, emptyGuid, toOrFromJobTypes } from '../../../util';
import { IFormFieldValidationConfig, IFormProps, isGreaterThanOrEqualTo, isNotBlank, runFieldValidation, runFormValidation } from '../../CoreLib/library';
import _ from 'lodash';
import { SelectChangeEvent } from '@mui/material';
import { AppraisalType } from '../../../dtos/generated/AppraisalType';
import { add } from 'date-fns';
import {
    useArchiveQuoteAttachmentsByIdMutation,
    useCreateQuoteAttachmentsMutation,
    useLazyGetQuoteAttachmentsQuery,
} from '../../../store/generated/generatedApi';

export interface IQuotePendingAttachmentFile {
    localId: string;
    file: File;
    previewUrl: string;
}

export interface IQuoteFormValues {
    quoteNumber?: number;
    quoteDate?: Date;
    expirationDate?: Date;
    maxExpirationDate?: Date;
    customerId: string;
    contactId?: string;
    effectiveOn?: Date;
    poNumber?: string;
    salesPersonId?: string;
    projectName?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    directions: string;
    miles?: number;
    minutes?: number;
    toOrFromJob?: string;
    isTaxable?: boolean;
    memo?: string;
    status?: string;
    isActive: boolean;
    type?: string;
    appraisalType?: AppraisalType;
    jobName?: string;
    siteName?: string;
    regionId?: string;
    region?: RegionDto | undefined;
    customerJobNumber?: string;
}

const DEFAULT_QUOTE: QuoteDto = {
    id: emptyGuid,
    quoteNumber: undefined,
    quoteDate: new Date(),
    expirationDate: add(new Date(), { months: 2 }),
    maxExpirationDate: undefined,
    customerId: emptyGuid,
    contactId: emptyGuid,
    effectiveOn: new Date(),
    poNumber: '',
    salesPersonId: emptyGuid,
    projectName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: 'IN',
    zipCode: '',
    country: countries[2], // US
    directions: '',
    miles: undefined,
    minutes: undefined,
    toOrFromJob: toOrFromJobTypes[0],
    isTaxable: false,
    memo: '',
    status: 'In Progress',
    isActive: true,
    createdOn: new Date(),
    clientId: emptyGuid,
    type: '',
    appraisalType: AppraisalType.Quote,
    jobName: '',
    siteName: '',
    region: undefined,
    customerJobNumber: '',
};

type QuoteFormHookProps = IFormProps<QuoteDto> & {
    onAfterSuccessfulCreate?: (quote: QuoteDto) => void;
};

export function useQuoteForm(props: QuoteFormHookProps) {
    const { save, cancel, initValues, onAfterSuccessfulCreate } = props;
    const [uploadAttachment] = useCreateQuoteAttachmentsMutation();
    const [deleteAttachment] = useArchiveQuoteAttachmentsByIdMutation();
    const [refetchQuoteAttachments] = useLazyGetQuoteAttachmentsQuery();
    const [formIsActive, setFormIsActive] = useState(initValues?.isActive ?? true);
    const [formQuoteNumber, setFormQuoteNumber] = useState(initValues?.quoteNumber);
    const [formQuoteDate, setFormQuoteDate] = useState<Date | null | undefined>(initValues?.quoteDate ?? new Date());
    const [formExpirationDate, setFormExpirationDate] = useState<Date | null | undefined>(initValues?.expirationDate ?? add(new Date(), { months: 2 }));
    const [formCustomer, setFormCustomer] = useState<CustomerDto | null>(initValues?.customer ?? null);
    const [formContact, setFormContact] = useState<ContactDto | null>(initValues?.contact ?? null);

    const [formEffectiveOn, setFormEffectiveOn] = useState<Date | null | undefined>(initValues?.effectiveOn ?? new Date());
    const [formPoNumber, setFormPoNumber] = useState(initValues?.poNumber || '');
    const [formProjectName, setFormProjectName] = useState(initValues?.projectName ?? '');
    const [formAddressLine1, setFormAddressLine1] = useState(initValues?.addressLine1 ?? '');
    const [formAddressLine2, setFormAddressLine2] = useState(initValues?.addressLine2 ?? '');
    const [formCity, setFormCity] = useState(initValues?.city ?? '');
    const [formState, setFormState] = useState(initValues?.state ?? 'IN');
    const [formZipCode, setFormZipCode] = useState(initValues?.zipCode ?? '');
    const [formCountry, setFormCountry] = useState(initValues?.country ?? countries[2]);
    const [formDirections, setFormDirections] = useState(initValues?.directions ?? '');
    const [formMiles, setFormMiles] = useState<number | undefined>(initValues?.miles ?? undefined);
    const [formMinutes, setFormMinutes] = useState<number | undefined>(initValues?.minutes ?? undefined);
    const [formToOrFromJob, setFormToOrFromJob] = useState(initValues?.toOrFromJob ?? '');
    const [formIsTaxable, setFormIsTaxable] = useState(initValues?.isTaxable ?? false);
    const [formSalesPerson, setFormSalesPerson] = useState<UserDto | null>(initValues?.salesPerson ?? null);
    const [formMemo, setFormMemo] = useState(initValues?.memo ?? '');
    const [formStatus, setFormStatus] = useState(initValues?.status ?? 'In Progress');
    const [formType, setFormType] = useState(initValues?.type ?? '');
    const [formAppraisalType, setFormAppraisalType] = useState<AppraisalType>(initValues?.appraisalType ?? AppraisalType.Quote);
    const [formJobName, setFormJobName] = useState(initValues?.jobName ?? '');
    const [formSiteName, setFormSiteName] = useState(initValues?.siteName ?? '');
    const [formSite, setFormSite] = useState<SiteDto | undefined>();
    const [formRegion, setFormRegion] = useState<RegionDto | undefined>(initValues?.region ?? undefined);
    const [formCustomerJobNumber, setFormCustomerJobNumber] = useState(initValues?.customerJobNumber ?? '');
    const [isSaved, setIsSaved] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<IQuotePendingAttachmentFile[]>([]);
    const [pendingRemovalAttachmentIds, setPendingRemovalAttachmentIds] = useState<string[]>([]);
    const [attachmentSaveError, setAttachmentSaveError] = useState<string | null>(null);
    const [attachmentSyncInProgress, setAttachmentSyncInProgress] = useState(false);
    const previousQuoteIdRef = useRef<string | undefined>(undefined);
    const [pendingPostCreateQuote, setPendingPostCreateQuote] = useState<QuoteDto | null>(null);

    const clearPendingPostCreateQuote = useCallback(() => {
        setPendingPostCreateQuote(null);
    }, []);

    const clearAttachmentStaging = useCallback(() => {
        setPendingAttachmentFiles((prev) => {
            prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
            return [];
        });
        setPendingRemovalAttachmentIds([]);
        setAttachmentSyncInProgress(false);
    }, []);

    useEffect(() => {
        const id = initValues?.id;
        if (previousQuoteIdRef.current !== undefined && previousQuoteIdRef.current !== id) {
            clearAttachmentStaging();
        }
        previousQuoteIdRef.current = id;
    }, [initValues?.id, clearAttachmentStaging]);

    const isMasterPriceListQuote = useMemo(() => {
        return formAppraisalType === AppraisalType.MasterPriceList;
    }, [formAppraisalType]);

    useEffect(() => {
        if (formCustomer) {
            setFormIsTaxable(initValues?.isTaxable ?? formCustomer?.isTaxable ?? false);
        }
    }, [formCustomer, initValues?.isTaxable]);

    const quoteLabel = useMemo(() => {
        if (formAppraisalType === AppraisalType.Estimate) {
            return 'Estimate';
        } else if (formAppraisalType === AppraisalType.Quote) {
            return 'Quote';
        } else {
            return 'MPL Quote';
        }
    }, [formAppraisalType]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof QuoteDto, string>>(
        new Map([
            ['customerId', ''],
            ['addressLine1', ''],
            ['city', ''],
            ['state', ''],
            ['zipCode', ''],
            ['country', ''],
            ['miles', ''],
            ['minutes', ''],
            ['type', ''],
            ['quoteDate', ''],
            ['effectiveOn', ''],
            ['expirationDate', ''],
            ['poNumber', ''],
        ])
    );

    const formFieldValidators = useMemo(() => {
        return new Map<keyof QuoteDto, IFormFieldValidationConfig>([
            [
                'customerId',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: 'Customer',
                },
            ],
            [
                'addressLine1',
                {
                    validators: isMasterPriceListQuote ? [] : [isNotBlank],
                    errorMessageEntityName: 'Address Line 1',
                },
            ],
            [
                'city',
                {
                    validators: isMasterPriceListQuote ? [] : [isNotBlank],
                    errorMessageEntityName: 'City',
                },
            ],
            [
                'state',
                {
                    validators: isMasterPriceListQuote ? [] : [isNotBlank],
                    errorMessageEntityName: 'State',
                },
            ],
            [
                'zipCode',
                {
                    validators: isMasterPriceListQuote ? [] : [isNotBlank],
                    errorMessageEntityName: 'Zip Code',
                },
            ],
            [
                'country',
                {
                    validators: isMasterPriceListQuote ? [] : [isNotBlank],
                    errorMessageEntityName: 'Country',
                },
            ],
            [
                'type',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: 'Type',
                },
            ],
            [
                'miles',
                {
                    validators: isMasterPriceListQuote ? [] : [isGreaterThanOrEqualTo(0)],
                    errorMessageEntityName: 'Miles',
                },
            ],
            [
                'minutes',
                {
                    validators: isMasterPriceListQuote ? [] : [isGreaterThanOrEqualTo(0)],
                    errorMessageEntityName: 'Minutes',
                },
            ],
            [
                'quoteDate',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: `${quoteLabel} Date`,
                },
            ],
            [
                'effectiveOn',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: 'Effective On',
                },
            ],
            [
                'expirationDate',
                {
                    validators: [isNotBlank], // Need date validation
                    errorMessageEntityName: 'Expiration Date',
                },
            ],
            [
                'poNumber',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: 'PO Number'
                }
            ],
        ]);
    }, [isMasterPriceListQuote, quoteLabel]);

    const getCurrentFormValues = useCallback((): IQuoteFormValues => {
        return {
            quoteNumber: formQuoteNumber,
            quoteDate: formQuoteDate ?? undefined,
            expirationDate: formExpirationDate ?? undefined,
            customerId: formCustomer?.id ?? emptyGuid,
            contactId: formContact?.id,
            effectiveOn: formEffectiveOn ?? undefined,
            poNumber: formPoNumber,
            projectName: formProjectName,
            addressLine1: formAddressLine1,
            addressLine2: formAddressLine2,
            city: formCity,
            state: formState,
            zipCode: formZipCode,
            country: formCountry,
            directions: formDirections,
            miles: formMiles,
            minutes: formMinutes,
            toOrFromJob: formToOrFromJob,
            isTaxable: formIsTaxable,
            salesPersonId: formSalesPerson?.id,
            memo: formMemo,
            status: formStatus,
            isActive: formIsActive,
            type: formType,
            appraisalType: formAppraisalType ? formAppraisalType : AppraisalType.Quote,
            jobName: formJobName,
            siteName: formSiteName,
            regionId: formRegion?.id,
            region: formRegion,
            customerJobNumber: formCustomerJobNumber,
        };
    }, [
        formQuoteNumber,
        formQuoteDate,
        formExpirationDate,
        formCustomer?.id,
        formContact?.id,
        formEffectiveOn,
        formPoNumber,
        formProjectName,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formMiles,
        formMinutes,
        formToOrFromJob,
        formIsTaxable,
        formSalesPerson?.id,
        formMemo,
        formStatus,
        formIsActive,
        formType,
        formAppraisalType,
        formJobName,
        formSiteName,
        formRegion,
        formCustomerJobNumber,
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<QuoteDto>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IQuoteFormValues) => {
            const validationConfig = formFieldValidators.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, formFieldValidators, getCurrentFormValues]
    );

    const hasPendingAttachmentChanges =
        pendingAttachmentFiles.length > 0 || pendingRemovalAttachmentIds.length > 0;

    const isFormDirty = useCallback(() => {
        if (hasPendingAttachmentChanges) {
            return true;
        }
        if (isSaved) {
            return false;
        }
        return isChanged;
    }, [hasPendingAttachmentChanges, isChanged, isSaved]);

    const addPendingAttachmentFiles = useCallback((files: File[]) => {
        setPendingAttachmentFiles((prev) => [
            ...prev,
            ...files.map((file) => ({
                localId: crypto.randomUUID(),
                file,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);
    }, []);

    const removePendingAttachmentFile = useCallback((localId: string) => {
        setPendingAttachmentFiles((prev) => {
            const found = prev.find((p) => p.localId === localId);
            if (found) {
                URL.revokeObjectURL(found.previewUrl);
            }
            return prev.filter((p) => p.localId !== localId);
        });
    }, []);

    const stageAttachmentRemoval = useCallback((attachmentId: string) => {
        setPendingRemovalAttachmentIds((prev) =>
            prev.includes(attachmentId) ? prev : [...prev, attachmentId],
        );
    }, []);

    const handleQuoteNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormQuoteNumber(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
        setIsChanged(true);
    }, []);

    const handleQuoteDateChange = useCallback((value: Date | null | undefined) => {
        setFormQuoteDate(value);
        setIsChanged(true);
    }, []);

    const handleExpirationDateChange = useCallback((value: Date | null | undefined) => {
        setFormExpirationDate(value);
        setIsChanged(true);
    }, []);

    const handleCustomerChange = useCallback(
        (value: any) => {
            setFormCustomer(value);
            setFormContact(null);
            setIsChanged(true);
        },
        []
    );

    const handleContactChange = useCallback((value: any) => {
        setFormContact(value);
        setIsChanged(true);
    }, []);

    const handleEffectiveOnDateChange = useCallback((value: Date | null | undefined) => {
        setFormEffectiveOn(value);
        setIsChanged(true);
    }, []);

    const handlePoNumberChange = useCallback(
        (value: string) => {
            setFormPoNumber(value);
            setIsChanged(true);
        },
        []
    );

    const handleCustomerJobNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerJobNumber(event.target.value);
        setIsChanged(true);
    }, []);

    const handleProjectNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormProjectName(event.target.value);
        setIsChanged(true);
    }, []);

    const handleAddressLine1Change = useCallback((value: string) => {
        setFormAddressLine1(value);
        setIsChanged(true);
    }, []);

    const handleAddressLine2Change = useCallback((value: string) => {
        setFormAddressLine2(value);
        setIsChanged(true);
    }, []);

    const handleCityChange = useCallback((value: string) => {
        setFormCity(value);
        setIsChanged(true);
    }, []);

    const handleStateChange = useCallback((value: string) => {
        setFormState(value);
        setIsChanged(true);
    }, []);

    const handleZipCodeChange = useCallback((value: string) => {
        setFormZipCode(value);
        setIsChanged(true);
    }, []);

    const handleCountryChange = useCallback((value: string) => {
        setFormCountry(value);
        setIsChanged(true);
    }, []);

    const handleDirectionsChange = useCallback((value: string) => {
        setFormDirections(value);
        setIsChanged(true);
    }, []);

    const handleMilesChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormMiles(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
        setIsChanged(true);
    }, []);

    const handleMinutesChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormMinutes(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
        setIsChanged(true);
    }, []);

    const handleToOrFromJobChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormToOrFromJob(event.target.value);
        setIsChanged(true);
    }, []);

    const handleFormTypeChange = useCallback((event: SelectChangeEvent) => {
        setFormType(event.target.value);
        setIsChanged(true);
    }, []);

    const handleIsTaxableChange = (_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsTaxable(isChecked);
        setIsChanged(true);
    };

    const handleSalesPersonChange = useCallback((value: UserDto) => {
        setFormSalesPerson(value);
        setIsChanged(true);
    }, []);

    const handleMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormMemo(event.target.value);
        setIsChanged(true);
    }, []);

    const handleStatusChange = useCallback((event: SelectChangeEvent) => {
        setFormStatus(event.target.value);
        setIsChanged(true);
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
        setIsChanged(true);
    }, []);

    const handleSiteNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFormSiteName(event.target.value);
        setIsChanged(true);
    };

    const handleRegionChange = (value: RegionDto) => {
        setFormRegion(value);
        setIsChanged(true);
    };

    const handleSiteChange = (value?: SiteDto) => {
        setFormSite(value);
        setFormSiteName(value?.name ?? '');
        setFormRegion(value?.region);
        setIsChanged(true);
        setFormAddressLine1(value?.addressLine1 ?? '');
        setFormAddressLine2(value?.addressLine2 ?? '');
        setFormCity(value?.city ?? '');
        setFormState(value?.state ?? '');
        setFormZipCode(value?.zipCode ?? '');
        setFormCountry(value?.country ?? '');
        setFormDirections(value?.directions ?? '');
    };

    const handleJobNameChange = useCallback((value: string) => {
        setFormJobName(value);
        setIsChanged(true);
    }, []);

    const handleSave = useCallback(async () => {
        const isFormValid = validateForm();
        if (!isFormValid) {
            return;
        }

        setAttachmentSaveError(null);

        const formValues = getCurrentFormValues();
        const updatedUserRecord = {
            ...(initValues ?? DEFAULT_QUOTE),
            ...formValues,
        };

        const hasPending =
            pendingAttachmentFiles.length > 0 || pendingRemovalAttachmentIds.length > 0;

        let savedDto: QuoteDto | void;
        try {
            const saveResult = save(updatedUserRecord);
            if (saveResult && typeof (saveResult as Promise<QuoteDto | void>).then === 'function') {
                savedDto = await (saveResult as Promise<QuoteDto | void>);
            } else {
                savedDto = undefined;
            }
        } catch {
            return;
        }

        const quoteId = savedDto?.id ?? updatedUserRecord.id;

        try {
            if (hasPending) {
                if (!quoteId || quoteId === emptyGuid) {
                    throw new Error('Quote must be saved before attachments can be uploaded.');
                }
                setAttachmentSyncInProgress(true);
                try {
                    for (const attachmentId of pendingRemovalAttachmentIds) {
                        await deleteAttachment({ quoteId, attachmentId }).unwrap();
                    }
                    for (const pf of pendingAttachmentFiles) {
                        const formData = new FormData();
                        formData.append('file', pf.file);
                        await uploadAttachment({ params: { quoteId }, payload: formData }).unwrap();
                    }
                    pendingAttachmentFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                    setPendingAttachmentFiles([]);
                    setPendingRemovalAttachmentIds([]);
                    try {
                        await refetchQuoteAttachments({ quoteId }).unwrap();
                    } catch {
                        /* non-fatal; mutations already invalidated cache */
                    }
                } finally {
                    setAttachmentSyncInProgress(false);
                }
            }

            setIsSaved(true);
            setIsChanged(false);

            const wasNewQuote = !initValues?.id || initValues.id === emptyGuid;
            if (wasNewQuote && savedDto?.id && savedDto.id !== emptyGuid && onAfterSuccessfulCreate) {
                setPendingPostCreateQuote({ ...updatedUserRecord, ...savedDto });
            }
        } catch {
            if (hasPending) {
                setAttachmentSaveError(
                    'Could not update attachments. The quote may have been saved; fix any issues and click Save again.',
                );
            }
        }
    }, [
        deleteAttachment,
        getCurrentFormValues,
        initValues,
        onAfterSuccessfulCreate,
        pendingAttachmentFiles,
        pendingRemovalAttachmentIds,
        refetchQuoteAttachments,
        save,
        uploadAttachment,
        validateForm,
    ]);

    const handleCancel = useCallback(() => {
        setAttachmentSaveError(null);
        clearAttachmentStaging();
        cancel();
    }, [cancel, clearAttachmentStaging]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        formIsActive,
        formQuoteNumber,
        formQuoteDate,
        formExpirationDate,
        formCustomer,
        formContact,
        formEffectiveOn,
        formPoNumber,
        formProjectName,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formMiles,
        formMinutes,
        formToOrFromJob,
        formIsTaxable,
        formSalesPerson,
        formMemo,
        formStatus,
        formType,
        formAppraisalType,
        formJobName,
        formSiteName,
        formSite,
        formRegion,
        formCustomerJobNumber,
        handleIsActiveChange,
        handleQuoteNumberChange,
        handleQuoteDateChange,
        handleExpirationDateChange,
        handleCustomerChange,
        handleContactChange,
        handleEffectiveOnDateChange,
        handlePoNumberChange,
        handleCustomerJobNumberChange,
        handleProjectNameChange,
        handleAddressLine1Change,
        handleAddressLine2Change,
        handleCityChange,
        handleStateChange,
        handleZipCodeChange,
        handleCountryChange,
        handleDirectionsChange,
        handleMilesChange,
        handleMinutesChange,
        handleToOrFromJobChange,
        handleIsTaxableChange,
        handleSalesPersonChange,
        handleMemoChange,
        handleStatusChange,
        handleFormTypeChange,
        handleSiteNameChange,
        handleRegionChange,
        handleSiteChange,
        validateField,
        setFormType,
        setFormAddressLine1,
        setFormCity,
        setFormCountry,
        setFormState,
        setFormZipCode,
        setFormAppraisalType,
        handleJobNameChange,
        setFormSalesPerson,
        quoteLabel,
        isMasterPriceListQuote,
        getCurrentFormValues,
        pendingAttachmentFiles,
        pendingRemovalAttachmentIds,
        addPendingAttachmentFiles,
        removePendingAttachmentFile,
        stageAttachmentRemoval,
        attachmentSaveError,
        attachmentSyncInProgress,
        pendingPostCreateQuote,
        clearPendingPostCreateQuote,
    };
}
